// src/annihilation/villageData.js
// ============================================================
//  ANNIHILATION — The Old Fold  (v2)
//  Single source of truth for the home village.
//  Canon: ANNIHILATION_WORLD_ATLAS (v2), Part 2.
//
//  Vyren pattern: the `id` / `screen` strings are INTERNAL and
//  never change. Display strings (name, role, flavor, lines)
//  are the only things that ever get renamed.
//
//  Consumers: App.js (placeholder rooms + routing) and Plaza.js
//  (destination arrays, villager dialogue, menu sections).
//  One source. No drift.
//
//  v2: villager dialogue `lines` added (Plaza walk-up speech).
//  Full DialogueEngine trees are writing work for a later batch —
//  these are the slow-drip one-liners until then.
// ============================================================

export const VILLAGE = {
  id: "old_fold",
  name: "The Old Fold",
  tagline: "The first village. The first safe place. The first home.",
  flavor:
    "An old word for a sheep enclosure. Pre-civilization folk kept what " +
    "they loved in a fold. The name is a promise: this is where what you " +
    "love is kept safe.",
};

// ------------------------------------------------------------
// Launch buildings — World Atlas v2, Part 2 (all eight).
// `screen` is the App.js route string each building resolves to.
// ------------------------------------------------------------
export const BUILDINGS = [
  {
    id: "bonfire",
    screen: "bonfire",
    name: "The Bonfire",
    keeper: null,
    role: "Rest point. Resets all rest-gated abilities.",
    flavor: "A great stone circle, constantly burning. Embers drift upward.",
  },
  {
    id: "marketplace",
    screen: "marketplace",
    name: "The Marketplace",
    keeper: "Maerie",
    role:
      "Buy gathering materials with Blood Crests (they feed the " +
      "Forge). Potions, loot-selling, and rotating Tome stock arrive " +
      "with later batches.",
    flavor: "Open-air stalls around the square. Merchants shouting.",
  },
  {
    id: "forge",
    screen: "forge",
    name: "Willow's Forge",
    keeper: "Willow",
    role: "Upgrade Tomes. Craft gear from gathered materials.",
    flavor: "Hammer-sounds and orange glow. Weapon racks line the walls.",
  },
  {
    id: "trainer",
    screen: "trainer",
    name: "The Trainer",
    keeper: "Sigfer",
    role: "Spend skill points. One single-point refund per village visit.",
    flavor: "A quiet stone hall. Training dummies stand outside.",
  },
  {
    id: "registrar",
    screen: "registrar",
    name: "The Registrar",
    keeper: "Otto",
    role: "Purchase additional character slots. Browse unlocked Beings.",
    flavor: "A small office of parchment scrolls, and a book of names.",
  },
  {
    id: "bulletin",
    screen: "bulletin",
    name: "The Bulletin",
    keeper: "Roan",
    role:
      "World events, season progress, Easter egg hints, and " +
      "community milestones.",
    flavor: "A large board at the square's edge, thick with pinned parchments.",
  },
  {
    id: "gate",
    screen: "gate",
    name: "The Gate",
    keeper: "Gatekeeper Vel",
    role: "Leave the village. Co-op matchmaking, invites, and parties.",
    flavor: "An imposing wooden archway at the village perimeter.",
  },
  {
    id: "garden",
    screen: "garden",
    name: "The Chao Garden",
    keeper: "Mother of Motes",
    role: "Passive Chao regeneration while in the village.",
    flavor: "Crystal flowers. Chao motes drifting upward. She never leaves it.",
  },
];

// ------------------------------------------------------------
// The seven named villagers — World Atlas v2, Part 2.
// `lines` are walk-up one-liners, spoken in Plaza toasts + the
// console log. Written to each villager's canonical personality;
// they state nothing about the world that the docs don't.
// ------------------------------------------------------------
export const NPCS = [
  {
    id: "willow",
    name: "Willow",
    building: "forge",
    role: "Forge-keeper. The village smith. A Willie — cousin to Terror's.",
    persona: "Gruff, patient, tells long stories about old clients.",
    lines: [
      "Had a client once who sharpened a blade so long there was nothing left of it. Patience, sure. Sense? No.",
      "Bring me something worth working. The fire doesn't care either way — but I do.",
      "My cousin runs a workshop far from here. Louder man than me. Louder everything, honestly.",
    ],
  },
  {
    id: "maerie",
    name: "Maerie",
    building: "marketplace",
    role: "Marketplace proprietor. Sells consumables and Tomes.",
    persona: "Sharp-tongued. Sees everyone, knows everyone's business.",
    lines: [
      "You were at the Bonfire twice today. Don't look surprised — I see everyone.",
      "Buy something or make room for someone who will. ...Kidding. Mostly.",
      "New stock when the week turns. Same as always. Some things in this village you can count on.",
    ],
  },
  {
    id: "sigfer",
    name: "Sigfer",
    building: "trainer",
    role: "The Trainer. Advises on builds. Refunds skill points.",
    persona: "Quiet, thoughtful, speaks in questions more than answers.",
    lines: [
      "You want more power. But do you know yet what you want it for?",
      "A point spent is a door opened. Which doors are you willing to leave closed?",
      "Regret one choice per visit — I allow that much. Any more, and were they really choices?",
    ],
  },
  {
    id: "otto",
    name: "Otto",
    building: "registrar",
    role: "The Registrar. Keeps the book of all registered beings.",
    persona: "Fussy, precise, obsessed with accuracy.",
    lines: [
      "Name. Spelled correctly, please. The book does not tolerate errata.",
      "Every being in these pages is exactly where it should be. I intend to keep it that way.",
      "One smudged entry, forty years ago. I still think about it.",
    ],
  },
  {
    id: "roan",
    name: "Roan",
    building: "bulletin",
    role: "Bulletin-keeper. Posts event news. Knows the rumors.",
    persona: "Young, enthusiastic, prone to exaggeration.",
    lines: [
      "Have you SEEN the board today? Okay, nothing new yet, but when there is — huge. HUGE.",
      "Someone said they saw something out past the Grove. Probably nothing. Probably EVERYTHING.",
      "I keep the pins straight and the parchment fresh. This board is the beating heart of the Fold. Ask anyone!",
    ],
  },
  {
    id: "vel",
    name: "Gatekeeper Vel",
    building: "gate",
    role: "Guards the village gate. Explains co-op and party flow.",
    persona: "Former Aenarchist. Scarred. Speaks like a soldier.",
    lines: [
      "Travel in fours if you can. The wilds don't respect courage. They respect numbers.",
      "I walked out there once. Came back different. Go ready or don't go.",
      "The Gate opens when your party's ready. All of you. That's the rule, and it's a good one.",
    ],
  },
  {
    id: "mother_of_motes",
    name: "Mother of Motes",
    building: "garden",
    role: "Tends the Chao Garden. Never seen to leave it.",
    persona: "Ancient. Speaks rarely — each word weighs like a coin.",
    lines: [
      "Rest.",
      "The flowers remember.",
      "Sit a while. The motes do the rest.",
    ],
  },
];

// ------------------------------------------------------------
// Lookups — one deriving function beats parallel copies.
// ------------------------------------------------------------
export const getBuildingForScreen = (screenId) =>
  BUILDINGS.find((b) => b.screen === screenId) || null;

export const getBuilding = (buildingId) =>
  BUILDINGS.find((b) => b.id === buildingId) || null;

export const getNpcForBuilding = (buildingId) =>
  NPCS.find((n) => n.building === buildingId) || null;
